// HKDSE 2015 Paper 1 Knowledge Graph Samples
// Organized by question types with systematic naming

export const hkdse2015Samples = {
  algebra: [
    {
      id: 'hkdse2015_algebra_01',
      problem: "Simplify $\\frac{m^9}{(m^3 n^{-7})^5}$ and express the answer in positive index form.",
      problemType: "algebra_indices",
      difficulty: "intermediate",
      description: "Simplifying expressions with negative indices",
      graphData: {
        nodes: [
          {
            id: "negative_indices",
            name: "Negative Indices",
            type: "concepts",
            description: "Understanding negative indices and their properties",
            value: "a⁻ⁿ = 1/aⁿ",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "power_of_power",
            name: "Power of Power",
            type: "concepts",
            description: "When raising a power to another power, multiply the indices",
            value: "(aᵐ)ⁿ = aᵐⁿ",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "division_of_powers",
            name: "Division of Powers",
            type: "concepts",
            description: "When dividing powers with the same base, subtract the indices",
            value: "aᵐ ÷ aⁿ = aᵐ⁻ⁿ",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_simplify_denominator",
            name: "Step 1: Simplify Denominator",
            type: "steps",
            description: "Apply power of power rule to (m³n⁻⁷)⁵",
            value: "(m³n⁻⁷)⁵ = m¹⁵n⁻³⁵",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_divide",
            name: "Step 2: Divide Numerator by Denominator",
            type: "steps",
            description: "Divide m⁹ by m¹⁵n⁻³⁵",
            value: "m⁹ ÷ (m¹⁵n⁻³⁵) = m⁻⁶n³⁵",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "step3_convert_negative",
            name: "Step 3: Convert to Positive Indices",
            type: "steps",
            description: "Convert negative indices to positive using reciprocal",
            value: "m⁻⁶n³⁵ = n³⁵/m⁶",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "The simplified expression in positive indices",
            value: "n³⁵/m⁶",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "negative_indices", to: "step3_convert_negative", type: "enables", strength: 10 },
          { from: "power_of_power", to: "step1_simplify_denominator", type: "enables", strength: 9 },
          { from: "division_of_powers", to: "step2_divide", type: "enables", strength: 9 },
          { from: "step1_simplify_denominator", to: "step2_divide", type: "leads to", strength: 10 },
          { from: "step2_divide", to: "step3_convert_negative", type: "leads to", strength: 10 },
          { from: "step3_convert_negative", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'hkdse2015_algebra_02',
      problem: "Given that $b$ is the subject of the formula $\\frac{4a + 5b - 7}{b} = 8$.",
      problemType: "algebra_formula",
      difficulty: "fundamental",
      description: "Rearranging formula to make a variable the subject",
      graphData: {
        nodes: [
          {
            id: "fraction_equation",
            name: "Fraction Equation",
            type: "concepts",
            description: "Equation with fraction involving variable b",
            value: "(4a + 5b - 7)/b = 8",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cross_multiplication",
            name: "Cross Multiplication",
            type: "concepts",
            description: "Method to eliminate fraction by multiplying both sides",
            value: "If a/b = c, then a = bc",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_cross_multiply",
            name: "Step 1: Cross Multiply",
            type: "steps",
            description: "Multiply both sides by b to eliminate fraction",
            value: "4a + 5b - 7 = 8b",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_collect_terms",
            name: "Step 2: Collect Terms",
            type: "steps",
            description: "Collect all terms with b on one side",
            value: "4a - 7 = 8b - 5b = 3b",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_solve_for_b",
            name: "Step 3: Solve for b",
            type: "steps",
            description: "Divide both sides by 3",
            value: "b = (4a - 7)/3",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "b expressed in terms of a",
            value: "b = (4a - 7)/3",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "fraction_equation", to: "cross_multiplication", type: "requires", strength: 9 },
          { from: "cross_multiplication", to: "step1_cross_multiply", type: "enables", strength: 10 },
          { from: "step1_cross_multiply", to: "step2_collect_terms", type: "leads to", strength: 9 },
          { from: "step2_collect_terms", to: "step3_solve_for_b", type: "leads to", strength: 10 },
          { from: "step3_solve_for_b", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  probability: [
    {
      id: 'hkdse2015_probability_01',
      problem: "Bag $A$ contains four cards with numbers $1$, $3$, $5$, and $7$ written on them respectively; while bag $B$ contains five cards with numbers $2$, $4$, $6$, $8$, and $10$ written on them respectively. If one card is randomly drawn from each bag, find the probability that the sum of the two numbers drawn is less than $9$.",
      problemType: "probability_combinations",
      difficulty: "intermediate",
      description: "Calculating probability using combinations",
      graphData: {
        nodes: [
          {
            id: "sample_space",
            name: "Sample Space",
            type: "concepts",
            description: "Total number of possible outcomes",
            value: "4 × 5 = 20 outcomes",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "favorable_outcomes",
            name: "Favorable Outcomes",
            type: "concepts",
            description: "Outcomes where sum < 9",
            value: "Count combinations with sum < 9",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "probability_formula",
            name: "Probability Formula",
            type: "formulas",
            description: "P(event) = favorable outcomes / total outcomes",
            value: "P = favorable/total",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_list_combinations",
            name: "Step 1: List All Combinations",
            type: "steps",
            description: "List all possible combinations and their sums",
            value: "(1,2)=3, (1,4)=5, (1,6)=7, (1,8)=9, (1,10)=11, etc.",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_count_favorable",
            name: "Step 2: Count Favorable",
            type: "steps",
            description: "Count combinations with sum < 9",
            value: "Favorable: (1,2), (1,4), (1,6), (3,2), (3,4), (5,2) = 6",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "step3_calculate_probability",
            name: "Step 3: Calculate Probability",
            type: "steps",
            description: "Divide favorable by total outcomes",
            value: "P = 6/20 = 3/10",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "The probability that sum < 9",
            value: "3/10",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "sample_space", to: "probability_formula", type: "enables", strength: 10 },
          { from: "favorable_outcomes", to: "probability_formula", type: "enables", strength: 10 },
          { from: "probability_formula", to: "step3_calculate_probability", type: "enables", strength: 10 },
          { from: "step1_list_combinations", to: "step2_count_favorable", type: "leads to", strength: 9 },
          { from: "step2_count_favorable", to: "step3_calculate_probability", type: "leads to", strength: 10 },
          { from: "step3_calculate_probability", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  factorization: [
    {
      id: 'hkdse2015_factorization_01',
      problem: "Factorize: (a) $x^3 + x^2 y - 7x^2$ (b) $x^3 + x^2 y - 7x^2 - x - y + 7$",
      problemType: "factorization",
      difficulty: "intermediate",
      description: "Factorization of polynomial expressions",
      graphData: {
        nodes: [
          {
            id: "common_factor",
            name: "Common Factor",
            type: "concepts",
            description: "Finding the greatest common factor",
            value: "GCF of terms",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "grouping_method",
            name: "Grouping Method",
            type: "concepts",
            description: "Grouping terms to find common factors",
            value: "Group terms with common factors",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_part_a",
            name: "Step 1: Part (a) - Find Common Factor",
            type: "steps",
            description: "Find GCF of x³, x²y, -7x²",
            value: "GCF = x²",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_part_a",
            name: "Step 2: Part (a) - Factor Out",
            type: "steps",
            description: "Factor out x² from each term",
            value: "x²(x + y - 7)",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_part_b",
            name: "Step 3: Part (b) - Group Terms",
            type: "steps",
            description: "Group terms: (x³ + x²y - 7x²) + (-x - y + 7)",
            value: "x²(x + y - 7) + (-x - y + 7)",
            importance: 8,
            x: 0, y: 100, z: -15
          },
          {
            id: "step4_part_b",
            name: "Step 4: Part (b) - Factor Further",
            type: "steps",
            description: "Factor out common terms from groups",
            value: "(x + y - 7)(x² - 1)",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Fully factorized expressions",
            value: "(a) x²(x + y - 7), (b) (x + y - 7)(x² - 1)",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "common_factor", to: "step1_part_a", type: "enables", strength: 10 },
          { from: "grouping_method", to: "step3_part_b", type: "enables", strength: 9 },
          { from: "step1_part_a", to: "step2_part_a", type: "leads to", strength: 10 },
          { from: "step2_part_a", to: "step3_part_b", type: "leads to", strength: 8 },
          { from: "step3_part_b", to: "step4_part_b", type: "leads to", strength: 9 },
          { from: "step4_part_b", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ]
}; 